import {
  businessForm,
  corporateForm,
  fundNecessityType,
  operationForm,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { z } from 'zod';

/// ////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////

export const reportStandardFundReportType = z.enum(['public', 'private']);
export const ReportStandardFundReportTypeLabelModifier = {
  public: t('report-standard-fund-type-public'),
  private: t('report-standard-fund-type-private'),
} as const satisfies Record<
  z.infer<typeof reportStandardFundReportType>,
  string
>;

export const reportStandardFundReportForPublicCsvHeader = z.enum([
  'fundSheetNumber',
  'ministryName',
  'additionalJurisdictionalMinistries',
  'fundBusinessName',
  'projectName',
  'fundOwner',
  'operationForms',
  'businessForms',
  'beginningOfTheFundBalance',
  'totalIncome',
  'totalExpenses',
  'administrativeExpense',
  'administrativeExpenseRate',
  'amountReturnedToTheNationalTreasury',
  'amountReturnedToTheOrganization',
  'endingOfTheFundBalance',
]);
export const reportStandardFundReportForPrivateCsvHeader = z.enum([
  'fundSheetNumber',
  'ministryName',
  'additionalJurisdictionalMinistries',
  'fundBusinessName',
  'projectName',
  'fundOwner',
  'corporateForm',
  'fiscalYearOfCreation',
  'endDate',
  'endDateOfNewApplicationReceiving',
  'operationForms',
  'businessForms',
  'necessityType',
  'hasLegalBases',
  'expenditurePurposes',
  'haveAppliedLawOfAppropriatenessOfSubsidies',
  'beginningOfTheFundBalance',
  'ownershipRatio',
]);
export const reportStandardFundReportForPrivateAnnualDataCsvHeader = z.enum([
  'totalIncome',
  'grantedAmountByTheCountryInitial',
  'grantedAmountByTheCountrySupplementary',
  'grantedAmountByTheCountryReserve',
  'grantedAmountByTheCountryAccountingNames',
  'totalExpenses',
  'amountReturnedToTheNationalTreasury',
  'amountReturnedToTheOrganization',
  'endingOfTheFundBalance',
  'nationalEquivalentOfTheBalance',
]);

/// ////////////////////////////////////////
/// Model
/// ////////////////////////////////////////

const OrganizationModel = z.object({ id: z.string().uuid(), name: z.string() });

export const ReportStandardFundForPublicModel = z.object({
  fundSheetNumber: z.number(),
  fundSheetBranchNumber: z.number().nullable(),
  ministryName: z.string(),
  additionalJurisdictionalMinistries: z.string().array().nullable(),
  fundBusinessName: z.string().nullable(),
  projectName: z.string(),
  fundOwner: z.string().nullable(),
  operationForms: z.array(operationForm).nullable(),
  businessForms: z.array(businessForm).nullable(),
  beginningOfTheFundBalance: z.number().nullable(),
  totalIncome: z.number().nullable(),
  negativeTotalIncomeCount: z.number(),
  totalExpenses: z.number().nullable(),
  negativeTotalExpensesCount: z.number(),
  administrativeExpense: z.number().nullable(),
  administrativeExpenseRate: z.number().nullable(),
  amountReturnedToTheNationalTreasury: z.number().nullable(),
  amountReturnedToTheOrganization: z.number().nullable(),
  endingOfTheFundBalance: z.number().nullable(),
  isCanceledEndingOfTheFundBalance: z.boolean(),
});

export const ReportStandardFundsForPublicModel = z.object({
  data: ReportStandardFundForPublicModel.array(),
  organizations: OrganizationModel.array(),
  summaryData: z.object({
    beginningOfTheFundBalanceSummary: z.number().nullable(),
    totalIncomeSummary: z.number().nullable(),
    totalExpensesSummary: z.number().nullable(),
    administrativeExpenseSummary: z.number().nullable(),
    amountReturnedToTheNationalTreasurySummary: z.number().nullable(),
    amountReturnedToTheOrganizationSummary: z.number().nullable(),
    endingOfTheFundBalanceSummary: z.number().nullable(),
  }),
});

export const ReportStandardFundForPrivateAnnualDataModel = z.object({
  fiscalYear: z.number(),
  beginningOfTheFundBalance: z.number().nullable(),
  totalIncome: z.number().nullable(),
  negativeTotalIncomeCount: z.number(),
  grantedAmountByTheCountryInitial: z.number().nullish(),
  grantedAmountByTheCountrySupplementary: z.number().nullish(),
  grantedAmountByTheCountryReserve: z.number().nullish(),
  grantedAmountByTheCountryAccountingNames: z.string().array().nullish(),
  totalExpenses: z.number().nullable(),
  negativeTotalExpensesCount: z.number(),
  amountReturnedToTheNationalTreasury: z.number().nullable(),
  amountReturnedToTheOrganization: z.number().nullable(),
  endingOfTheFundBalance: z.number().nullable(),
  isCanceledEndingOfTheFundBalance: z.boolean(),
  nationalEquivalentOfTheBalance: z.number().nullable(),
});

export const ReportStandardFundForPrivateModel = z.object({
  fundSheetNumber: z.number(),
  fundSheetBranchNumber: z.number().nullable(),
  ministryName: z.string(),
  additionalJurisdictionalMinistries: z.string().array().nullable(),
  fundBusinessName: z.string().nullable(),
  projectName: z.string(),
  fundOwner: z.string().nullable(),
  corporateForm: corporateForm.nullable(),
  // PRZ-885 の対応前に処理された帳票に対する後方互換を保つために nullable ではなく nullish にしている
  optionalCorporateForm: z.string().nullish(),
  fiscalYearOfCreation: z.number().nullable(),
  endDate: z.string().nullable(),
  endDateOfNewApplicationReceiving: z.string().nullable(),
  operationForms: z.array(operationForm).nullable(),
  businessForms: z.array(businessForm).nullable(),
  necessityType: fundNecessityType.nullable(),
  hasLegalBases: z.boolean(),
  expenditurePurposes: z.string().array().nullable(),
  haveAppliedLawOfAppropriatenessOfSubsidies: z.boolean().nullable(),
  ownershipRatio: z.number().nullable(),
  annualData: ReportStandardFundForPrivateAnnualDataModel.array(),
});
const StandardFundForPrivateSummaryModel = z.object({
  fiscalYear: z.number(),
  beginningOfTheFundBalanceSummary: z.number().nullable(),
  totalIncomeSummary: z.number().nullable(),
  grantedAmountByTheCountryInitialSummary: z.number().nullable(),
  grantedAmountByTheCountrySupplementarySummary: z.number().nullable(),
  grantedAmountByTheCountryReserveSummary: z.number().nullable(),
  totalExpensesSummary: z.number().nullable(),
  amountReturnedToTheNationalTreasurySummary: z.number().nullable(),
  amountReturnedToTheOrganizationSummary: z.number().nullable(),
  endingOfTheFundBalanceSummary: z.number().nullable(),
  nationalEquivalentOfTheBalanceSummary: z.number().nullable(),
});
export const ReportStandardFundsForPrivateModel = z.object({
  data: ReportStandardFundForPrivateModel.array(),
  summaryData: StandardFundForPrivateSummaryModel.array(),
  organizations: OrganizationModel.array(),
});

export const ReportStandardFundsParamsModel = z.object({
  isAllOrganization: z.boolean().nullish(),
  reportType: reportStandardFundReportType,
  fiscalYear: z.any().transform((v) => Number(v)),
});

/// ////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////

export const ReportStandardFundsFilterSchema = z.object({
  fiscalYear: z.any().transform((v) => Number(v)),
  organizationIds: z
    .union([z.literal('all'), z.string().uuid()])
    .array()
    .min(1),
  reportType: reportStandardFundReportType,
});
